import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { CourseSidebar } from "../types/lectureTypes";
import { MarkComplete } from "./markComplete";

interface CourseSidebarWithPermissionProps {
  courseSidebar: CourseSidebar;
  currentLectureId: string;
  isCompleted: boolean;
}

export function CourseSidebarWithPermission({
  courseSidebar,
  currentLectureId,
}: CourseSidebarWithPermissionProps) {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(
      courseSidebar.sections
        .filter((section) =>
          section.lectures.some((lecture) => lecture.id === currentLectureId)
        )
        .map((section) => section.id)
    )
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const formatDuration = (duration: string) => {
    // TimeSpan formatı: "00:15:30" -> "15dk 30sn"
    const parts = duration.split(":");
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);
    const seconds = parseInt(parts[2]);

    if (hours > 0) {
      return t("lecture.duration.hoursMinutes", { hours, minutes });
    }
    if (minutes > 0) {
      return t("lecture.duration.minutesSeconds", { minutes, seconds });
    }
    return t("lecture.duration.seconds", { seconds });
  };

  const totalLectures = courseSidebar.sections.reduce(
    (total, section) => total + section.lectures.length,
    0
  );

  const completedLectures = courseSidebar.sections.reduce(
    (total, section) =>
      total + section.lectures.filter((lecture) => lecture.isCompleted).length,
    0
  );

  return (
    <div className="h-full flex flex-col">
      <CardHeader className="border-b border-gray-200 dark:border-gray-700">
        <CardTitle className="text-gray-900 dark:text-white">
          {courseSidebar.name}
        </CardTitle>
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{t("lecture.sidebar.progress")}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {t("lecture.sidebar.lecturesProgress", { completed: completedLectures, total: totalLectures })}
            </span>
          </div>
          <Progress value={courseSidebar.courseProgress || 0} className="h-2" />
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            {t("lecture.sidebar.percentCompleted", { percent: Math.round(courseSidebar.courseProgress || 0) })}
          </p>
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto p-0">
        {courseSidebar.sections.map((section) => {
          const isExpanded = expandedSections.has(section.id);

          return (
            <div
              key={section.id}
              className="border-b border-gray-200 dark:border-gray-700 last:border-b-0"
            >
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                    <span className="font-medium text-gray-900 dark:text-white">
                      {section.name}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t("lecture.sidebar.lectureCount", { count: section.lectures.length })}
                  </span>
                </div>
              </button>

              {/* Section Lectures */}
              {isExpanded && (
                <div className="pb-2">
                  {section.lectures.map((lecture, index) => {
                    const isCurrentLecture = lecture.id === currentLectureId;

                    return (
                      <div key={lecture.id}>
                        {index > 0 && (
                          <hr className="border-gray-200 dark:border-gray-700" />
                        )}
                        <div
                          className={cn(
                            "px-6 py-3 border-l-2 transition-colors group",
                            isCurrentLecture
                              ? "border-primary bg-primary/5 dark:bg-primary/10"
                              : "border-transparent hover:bg-gray-50 dark:hover:bg-gray-700"
                          )}
                        >
                        <div className="flex items-center gap-3">
                          <div>
                            <MarkComplete
                              lectureId={lecture.id}
                              isCompleted={lecture.isCompleted}
                            />
                          </div>

                          <Link
                            to={`/course/${courseSidebar.id}/${lecture.id}`}
                            className="block"
                          >
                            <div className="flex-grow">
                              <p
                                className={cn(
                                  "text-sm font-medium",
                                  isCurrentLecture
                                    ? "text-primary"
                                    : "text-gray-900 dark:text-white"
                                )}
                              >
                                {lecture.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDuration(lecture.duration)}
                              </p>
                            </div>
                          </Link>
                        </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </div>
  );
}
